import { decode } from 'html-entities';

// Where html-entities departs from the HTML spec, and so from the browser:
// it maps `&lang;`/`&rang;` to the deprecated U+2329/U+232A instead of
// U+27E8/U+27E9, and decodes references to surrogate code points instead of
// replacing them with U+FFFD. Applied before it runs, so both sides agree.
const SPEC_ANGLE_BRACKETS = /&([lr])ang;/g;
const SURROGATE_REFERENCE = /&#(?:x(d[89a-f][0-9a-f]{2})|(5[5-7]\d{3}));?/gi;

const isSurrogate = (codePoint: number) => codePoint >= 0xd800 && codePoint <= 0xdfff;

const decodeOnServer = (text: string) =>
	decode(
		text
			.replace(SPEC_ANGLE_BRACKETS, (_, side) => (side === 'l' ? '⟨' : '⟩'))
			.replace(SURROGATE_REFERENCE, (reference, hex, decimal) =>
				isSurrogate(hex ? parseInt(hex, 16) : Number(decimal)) ? '�' : reference
			)
	);

let textarea: HTMLTextAreaElement | undefined;

/**
 * Decodes the HTML entities authors type into Markdown (`&amp;`, `&eacute;`,
 * `&#39;`, `&copy`…), which marked leaves as-is in text tokens and Svelte
 * would otherwise print literally.
 *
 * The server uses html-entities, whose full HTML5 entity table is ~8 KiB gzip.
 * The browser already has that table: a textarea's content is parsed as text
 * with entities decoded (no element is ever created from it, so markup in the
 * input stays text), which gives the same result and keeps html-entities out
 * of the client bundle. `import.meta.env.SSR` is a build-time constant, so
 * each build only contains its own branch.
 */
export const decodeHtmlEntities = (text: string) => {
	if (!text.includes('&')) return text;

	if (import.meta.env.SSR) return decodeOnServer(text);

	textarea ??= document.createElement('textarea');
	textarea.innerHTML = text;

	return textarea.value;
};
